import { LitElement, html, css, unsafeCSS } from 'lit';
import styles from './catalog-basket-drag.scss?inline';
import debounce from 'lodash/debounce';
import throttle from 'lodash/throttle';
import { USER_SELECT_NONE_CLASS } from '@/components/catalog/constants.js';

export default class SotbitCatalogBasketDrag extends LitElement {
    static styles = [
        css`
            ${unsafeCSS(styles)}
        `,
    ];

    static properties = {
        quantity: { type: String },
        price: { type: String },
        basketLink: { type: String, attribute: 'basket-link' },
        key: { type: String },
        isDragging: { type: Boolean, state: true },
        _setUserSelectNone: { type: Boolean, state: true },
    };

    constructor() {
        super();

        this.quantity = '0';
        this.price = '';
        this.basketLink = '';
        this.isDragging = false;
        this.offsetStore = { x: 0, y: 0, windowInnerWidth: 0, windowInnerHeight: 0 };
        this.offset = { x: 0, y: 0, windowInnerWidth: 0, windowInnerHeight: 0 };
        this.key = 'basketDragAndDrop';
        this._setUserSelectNone = false;

        this._handleMouseMoveBind = throttle(this._handleMouseMove.bind(this), 20);
        this._handleMouseUpBind = this._handleMouseUp.bind(this);
        this._handleTouchMoveBind = throttle(this._handleTouchMove.bind(this), 20);
        this._handleTouchEndBind = this._handleTouchEnd.bind(this);
        this._handleResizeBind = debounce(this._handleResize.bind(this), 100);
    }

    connectedCallback() {
        super.connectedCallback();

        const storageData = JSON.parse(localStorage.getItem(this.key));

        if (storageData) {
            Object.assign(this.offset, storageData);
            Object.assign(this.offsetStore, storageData);
            this.initPosition();
        }

        window.addEventListener('resize', this._handleResizeBind);
    }

    disconnectedCallback() {
        super.disconnectedCallback();

        window.removeEventListener('resize', this._handleResizeBind);
    }

    initPosition() {
        const windowInnerWidth = window.innerWidth;
        const windowInnerHeight = window.innerHeight;

        const xRatio = this.offset.x / this.offset.windowInnerWidth;
        const x = windowInnerWidth * xRatio;
        const yRatio = this.offset.y / this.offset.windowInnerHeight;
        const y = windowInnerHeight * yRatio;

        this._moveTo(x, y);
    }

    _startDrag(x, y) {
        if (this.isDragging) {
            return;
        }

        this.isDragging = true;
        this.rect = this.getBoundingClientRect();
        this.offset.x = Math.max(x, this.rect.left) - Math.min(x, this.rect.left);
        this.offset.y = Math.max(y, this.rect.top) - Math.min(y, this.rect.top);

        if (!document?.body?.classList?.contains(USER_SELECT_NONE_CLASS)) {
            document?.body?.classList?.add(USER_SELECT_NONE_CLASS);
            this._setUserSelectNone = true;
            if (window?.getSelection) {
                window?.getSelection()?.removeAllRanges();
            } else if (document?.selection) {
                document?.selection?.empty();
            }
        }
    }

    _moveDrag(x, y) {
        if (!this.isDragging) {
            return;
        }

        const maxX = window.innerWidth - (this.rect?.width || 0);
        const maxY = window.innerHeight - (this.rect?.height || 0);

        x = Math.max(0, Math.min(x - this.offset.x, maxX));
        y = Math.max(0, Math.min(y - this.offset.y, maxY));

        this._moveTo(x, y);
    }

    _moveTo(x, y) {
        this.offsetStore.x = x;
        this.offsetStore.y = y;

        this.style.left = `${this.offsetStore.x}px`;
        this.style.top = `${this.offsetStore.y}px`;
        this.style.bottom = 'auto';

        if (!this.hasAttribute('moved')) {
            this.setAttribute('moved', '');
        }
    }

    _endDrag() {
        if (!this.isDragging) {
            return;
        }

        if (this._setUserSelectNone) {
            document?.body?.classList?.remove(USER_SELECT_NONE_CLASS);
            this._setUserSelectNone = false;
        }

        this.isDragging = false;

        this.offsetStore.windowInnerWidth = window.innerWidth;
        this.offsetStore.windowInnerHeight = window.innerHeight;

        Object.assign(this.offset, this.offsetStore);

        localStorage.setItem(this.key, JSON.stringify(this.offsetStore));
    }

    _handleMouseDown(e) {
        this._startDrag(e.clientX, e.clientY);

        document.addEventListener('mousemove', this._handleMouseMoveBind);
        document.addEventListener('mouseup', this._handleMouseUpBind);
    }

    _handleMouseMove(e) {
        this._moveDrag(e.clientX, e.clientY);
    }

    _handleMouseUp() {
        this._endDrag();

        document.removeEventListener('mousemove', this._handleMouseMoveBind);
        document.removeEventListener('mouseup', this._handleMouseUpBind);

        localStorage.setItem(this.key, JSON.stringify(this.offsetStore));
    }

    _handleTouchStart(e) {
        const touch = e.touches[0];
        this._startDrag(touch.clientX, touch.clientY);

        document.addEventListener('touchmove', this._handleTouchMoveBind, {
            passive: false,
        });
        document.addEventListener('touchend', this._handleTouchEndBind);
    }

    _handleTouchMove(e) {
        e.preventDefault();
        const touch = e.touches[0];
        this._moveDrag(touch.clientX, touch.clientY);
    }

    _handleTouchEnd() {
        document.removeEventListener('touchmove', this._handleTouchMoveBind);
        document.removeEventListener('touchend', this._handleTouchEndBind);

        this._endDrag();
    }

    _handleResize() {
        if (!this.hasAttribute('moved')) {
            return;
        }

        this.initPosition();
    }

    render() {
        return html`<div class="basket">
            <sotbit-button-icon
                class="basket-drag${this.isDragging ? ' active' : ''}"
                @mousedown="${this._handleMouseDown}"
                @touchstart="${this._handleTouchStart}"
                theme="dark"
                variant="secondary"
                background
            >
                <sotbit-icon slot="content" size="s" icon="drag-indicator" no-padding></sotbit-icon>
            </sotbit-button-icon>
            <a class="basket-info" href="${this.basketLink}" ?disabled="${this.isDragging}">
                <span class="basket-info-icon">
                    <sotbit-button-icon theme="dark" variant="secondary" badge badge-numeric="${this.quantity}">
                        <sotbit-icon slot="content" size="l" icon="shopping-cart" no-padding></sotbit-icon>
                    </sotbit-button-icon>
                </span>
                <span>${this.price}</span>
            </a>
        </div>`;
    }
}
